import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  HeadingText,
  Card,
  Link,
  CaptionText,
  BodyText,
} from '@paypalcorp/pp-react';
import GivingSummary from '../givingSummary/givingSummary';
import styles from './givingSummaryCard.less';
import * as analytics from '../../lib/analytics';
import { getAccountType } from '../../lib/analytics';

const GivingSummaryCard = ({
  i18n,
  csrf,
  supportedProduct,
  preferredCharity,
  isCountrySetPreferredSupported,
  givingInsights = {},
  authState,
  cdnHost,
  contentCountry,
  isEngaged,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const closeSheet = () => setIsSheetOpen(false);
  const openSheet = () => {
    setIsSheetOpen(true);
  };

  const title = i18n('label.summary-title');
  const charitiesSupport =
    supportedProduct === 'all' || supportedProduct === 'charities';
  const fundraiserSupport =
    supportedProduct === 'all' || supportedProduct === 'fundraisers';

  const {
    firstComponentMessage,
    firstComponentAmount,
    secondComponentMessage,
    secondComponentAmount,
    deepLinkTitle,
    instrumentationData,
  } = givingInsights;

  const defaultFPTITags = {
    state: isEngaged ? 'engaged' : 'new',
    account_type: getAccountType(authState),
    event_source: 'web',
    acct_cntry: contentCountry,
    product: 'dw_giving',
  };

  useEffect(() => {
    if (instrumentationData) {
      const fpti_data = JSON.parse(instrumentationData);
      const { tile_id, card_type, scope, module } = fpti_data[0];

      analytics.instance.recordEvent(analytics.events.DW_GIVING_TILE_SHOWN, {
        ...defaultFPTITags,
        item_name: 'dw_giving',
        tile_id: tile_id,
        card_type: card_type,
        scope: scope,
        module: module,
        e: 'im',
      });
    }
  }, []);

  const handleReviewImpactClick = () => {
    openSheet();
    const fpti_data = JSON.parse(instrumentationData);
    const { tile_id, card_type, scope, module, link_name } = fpti_data[0];

    analytics.instance.recordEvent(analytics.events.DW_GIVING_TILE_PRESSED, {
      item_name: 'dw_giving',
      tile_id: tile_id,
      card_type: card_type,
      scope: scope,
      module: module,
      e: 'cl',
      link_name: link_name,
    });
  };

  return (
    <div className={styles.givingSummary} data-nemo='giving-summary-tile'>
      <div className={styles.title}>
        <BodyText>{title}</BodyText>
      </div>
      <Card className={styles.card}>
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.donated}>
              <CaptionText>{firstComponentMessage}</CaptionText>
              <HeadingText size='sm'>{firstComponentAmount}</HeadingText>
            </div>
            <div className={styles.raised}>
              <CaptionText>{secondComponentMessage}</CaptionText>
              <HeadingText size='sm'>{secondComponentAmount}</HeadingText>
            </div>
          </div>
          <div className={styles.footer}>
            <Link
              size='sm'
              onClick={handleReviewImpactClick}
              data-nemo='review-impact-link'
            >
              {deepLinkTitle}
            </Link>
          </div>
        </div>
        <div className={styles.illustration}>
          <img
            src={`https://${cdnHost}/paypal-ui/pictograms/multicolored/svg/trust.svg`}
          />
        </div>
      </Card>
      <GivingSummary
        isEngaged={isEngaged}
        charitiesSupport={charitiesSupport}
        fundraiserSupport={fundraiserSupport}
        preferredCharity={preferredCharity}
        isCountrySetPreferredSupported={isCountrySetPreferredSupported}
        isSheetOpen={isSheetOpen}
        closeSheet={closeSheet}
        defaultFPTITags={defaultFPTITags}
        i18n={i18n}
        csrf={csrf}
        cdnHost={cdnHost}
      />
    </div>
  );
};

GivingSummaryCard.propTypes = {
  i18n: PropTypes.any,
  csrf: PropTypes.string,
  supportedProduct: PropTypes.string,
  preferredCharity: PropTypes.object,
  isCountrySetPreferredSupported: PropTypes.bool,
  givingInsights: PropTypes.object,
  authState: PropTypes.string,
  cdnHost: PropTypes.string,
  contentCountry: PropTypes.string,
  isEngaged: PropTypes.bool,
};

export default GivingSummaryCard;
